import { useEffect, useState } from "react";
import { Activity, CheckCircle2, Mail, MailX, Pencil, ShieldAlert, Send, XCircle, Ban, History } from "lucide-react";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/utils2";

const ICONS = {
  submitted: Send,
  created_by_admin: Send,
  approved: CheckCircle2,
  rejected: XCircle,
  cancelled: Ban,
  amended: Pencil,
  clash_override: ShieldAlert,
  email_sent: Mail,
  email_failed: MailX,
  email_mocked: Mail,
};

const COLORS = {
  submitted: "text-[#C0C0C0] bg-[#141414] border-[#2A2A2A]",
  created_by_admin: "text-[#C0C0C0] bg-[#141414] border-[#2A2A2A]",
  approved: "text-[#34D399] bg-[#0F1F14] border-[#1F5E3D]",
  rejected: "text-[#F87171] bg-[#1F0A0A] border-[#5E1F1F]",
  cancelled: "text-[#A8A8A8] bg-[#141414] border-[#2A2A2A]",
  amended: "text-[#FCD34D] bg-[#1F1808] border-[#8A6E1F]",
  email_sent: "text-[#C0C0C0] bg-[#141414] border-[#2A2A2A]",
  email_failed: "text-[#F87171] bg-[#1F0A0A] border-[#5E1F1F]",
  email_mocked: "text-[#A8A8A8] bg-[#141414] border-[#2A2A2A]",
};

/**
 * Combined audit + email timeline for a holiday request.
 * Fetches /api/holiday-requests/{id}/timeline and renders a vertical timeline.
 */
export default function RequestTimeline({ requestId, refreshKey }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!requestId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/holiday-requests/${requestId}/timeline`)
      .then(({ data }) => { if (!cancelled) setEvents(data.events || []); })
      .catch(() => { if (!cancelled) setEvents([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [requestId, refreshKey]);

  if (!requestId) return null;

  return (
    <div className="space-y-2" data-testid="request-timeline">
      <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.08em] text-muted-foreground font-semibold">
        <History className="w-3.5 h-3.5"/> Full history
        {loading && <span className="ml-2 normal-case font-normal text-[10px]">loading…</span>}
      </div>
      {events.length === 0 && !loading && (
        <div className="text-sm text-muted-foreground py-2">No history yet.</div>
      )}
      <ol className="relative border-l-2 border-border ml-1.5 pl-3 space-y-2.5" data-testid="timeline-list">
        {events.map((e, i) => {
          const Icon = ICONS[e.action] || Activity;
          const color = COLORS[e.action] || "text-[#D0D0D0] bg-[#141414] border-[#2A2A2A]";
          return (
            <li key={i} className="relative" data-testid={`timeline-item-${i}`}>
              <span className={`absolute -left-[22px] top-0.5 w-5 h-5 rounded-full border flex items-center justify-center ${color}`}>
                <Icon className="w-3 h-3"/>
              </span>
              <div className="text-sm">
                <span className="font-medium capitalize">{e.action.replace(/_/g, " ")}</span>
                {e.actor && <span className="text-muted-foreground"> · {e.actor}</span>}
              </div>
              {e.details && <div className="text-xs text-muted-foreground mt-0.5 break-words">{e.details}</div>}
              <div className="text-[10px] uppercase tracking-[0.06em] text-muted-foreground/80 mt-0.5">{fmtDateTime(e.at)}</div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
